import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { getDisplayName } from "../utils/metalNameFixer";
import { FaArrowLeft } from "react-icons/fa";
import API_BASE from "../config/api";
import { formatTradeId } from "../utils/tradeIdFormatter";


const UserTradeHistory = () => {
  const [trades, setTrades] = useState([]);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    const fetchTrades = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_BASE}/api/trades/my`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();
        // Only show trades if payment verification proof was submitted
        const validTrades = Array.isArray(data)
          ? data.filter((t) => t.status && t.status !== "pending_payment")
          : [];
        setTrades(validTrades);
      } catch (err) {
        console.error("Failed to load trades:", err.message);
      }
    };

    fetchTrades();
  }, [user, navigate]);

  return (
    <PageWrapper>
      <TopBar>
        <BackBtn onClick={() => navigate("/")}>
          <FaArrowLeft /> <span>Home</span>
        </BackBtn>
        <Title>🧾 Your Trade History</Title>
      </TopBar>

      {trades.length === 0 ? (
        <NoTradesText>No trades yet.</NoTradesText>
      ) : (
        <StyledTable>
          <thead>
            <tr>
              <th>Trade ID</th>
              <th>Metal</th>
              <th>Type</th>
              <th>Qty</th>
              <th>Rate</th>
              <th>Total</th>
              <th>Status / Payment</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t, i) => {
              const status = t.status || "confirmed";
              const tradeId = formatTradeId(t, i, trades.length);
              return (
                <tr key={i}>
                  <td>
                    <span style={{ color: "#f5d76e", fontWeight: 700, letterSpacing: "0.5px" }}>
                      {tradeId}
                    </span>
                  </td>
                  <td>{getDisplayName(t.metal)}</td>
                  <td className={t.action === "buy" ? "buy" : "sell"}>
                    {t.action}
                  </td>
                  <td>{t.quantity}</td>
                  <td>₹ {t.rate}</td>
                  <td className="green">₹ {Number(t.totalPrice || 0).toLocaleString()}</td>
                  <td>
                    {status === "confirmed" && (
                      <span className="status-badge confirmed">
                        ✅ Confirmed
                      </span>
                    )}
                    {status === "pending_verification" && (
                      <span
                        className="status-badge pending clickable"
                        onClick={() => navigate(`/payment/${t._id}`)}
                        title="Click to view submitted payment receipt"
                      >
                        ⏳ Pending Verification
                      </span>
                    )}
                    {status === "declined" && (
                      <div className="status-declined-wrap">
                        <span className="status-badge declined">❌ Declined</span>
                        {t.declineReason && (
                          <span className="reason-sub">{t.declineReason}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td>{new Date(t.timestamp).toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </StyledTable>
      )}
    </PageWrapper>
  );
};

export default UserTradeHistory;

// ---------------------- Styled Components ----------------------

const PageWrapper = styled.div`
  background: #000;
  color: #f5d76e;
  min-height: 100vh;
  padding: 1rem 1rem 4rem;
  font-family: 'Poppins', sans-serif;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  background: #f5d76e;
  color: #000;
  border: none;
  padding: 8px 14px;
  font-weight: bold;
  border-radius: 5px;
  cursor: pointer;
  gap: 8px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: bold;
`;

const NoTradesText = styled.p`
  font-size: 1rem;
  color: #ccc;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
  background: #111;
  border: 1px solid #333;

  th, td {
    padding: 10px;
    text-align: center;
    border: 1px solid #333;
  }

  thead {
    background: #f5d76e;
    color: #000;
    font-weight: bold;
  }

  tbody tr:nth-child(even) {
    background: #1a1a1a;
  }

  tbody tr:hover {
    background: #2c2c2c;
  }

  .buy {
    color: #4caf50;
    font-weight: bold;
    text-transform: uppercase;
  }

  .sell {
    color: #f44336;
    font-weight: bold;
    text-transform: uppercase;
  }

  .green {
    color: #00e676;
    font-weight: bold;
  }

  .status-badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 700;

    &.confirmed {
      background: rgba(16, 185, 129, 0.15);
      color: #10b981;
      border: 1px solid rgba(16, 185, 129, 0.4);
    }

    &.pending {
      background: rgba(245, 215, 110, 0.15);
      color: #f5d76e;
      border: 1px solid rgba(245, 215, 110, 0.4);
    }

    &.declined {
      background: rgba(239, 68, 68, 0.15);
      color: #ef4444;
      border: 1px solid rgba(239, 68, 68, 0.4);
    }

    &.clickable {
      cursor: pointer;
      text-decoration: underline;
    }
  }

  .status-declined-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;

    .reason-sub {
      font-size: 0.72rem;
      color: #fca5a5;
      max-width: 150px;
    }
  }

  .pay-btn {
    background: #f5d76e;
    color: #000;
    border: none;
    padding: 4px 10px;
    border-radius: 6px;
    font-weight: 700;
    font-size: 0.78rem;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(245, 215, 110, 0.3);

    &:hover {
      background: #eab308;
    }
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;

    th, td {
      padding: 8px;
    }

    thead {
      font-size: 0.9rem;
    }
  }

  @media (max-width: 480px) {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
`;

